import React from 'react';
import {
  HomeIcon,
  AppointmentIcon,
  QAIcon,
  VoicemailIcon,
  ImportIcon,
  SettingsIcon,
  PatientsIcon,
  DoctorIcon,
  CallLogIcon,
  MediaIcon,
  ConversationIcon,
  LeaveMessageIcon,
  UsersIcon,
  ReminderIcon,
  SubscriptionIcon,
  ChartBarIcon,
  WhatsAppIcon,
  ToolboxIcon,
  BlockScheduleIcon
} from '../assets/icons';

// Import page components
import Home from '../pages/Home';
import Calendar from '../pages/Calendar';
import Patients from '../pages/Patients';
import PatientDetails from '../pages/PatientDetails';
import Doctors from '../pages/Doctors';
import Locations from '../pages/Locations';
import Policy from '../pages/Policy';
import Recordings from '../pages/Recordings';
import RecordingDetail from '../pages/RecordingDetail';
import CallLogs from '../pages/CallLogs';
import Media from '../pages/Media';
import Import from '../pages/Import';
import Settings from '../pages/Settings';
import Conversations from '../pages/Conversations';
import ConversationActivities from '../pages/ConversationActivities';
import Users from '../pages/Users';
import AppointmentReminderJobs from '../pages/AppointmentReminderJobs';
import AppointmentReminderJobEntries from '../pages/AppointmentReminderJobEntries';
import AppointmentReminderSettings from '../pages/AppointmentReminderSettings';
import Subscriptions from '../pages/Subscriptions';
import Analytics from '../pages/Analytics';
import MetaWhatsAppTemplates from '../pages/MetaWhatsAppTemplates';
import EmbeddedSignup from '../pages/EmbeddedSignup';
import Toolbox from '../pages/Toolbox';

/**
 * Navigation configuration that controls both routes and sidebar navigation
 * Set enabled: false to disable a feature completely
 */
export const navigationConfig = [
  {
    id: 'home',
    path: '/home',
    component: Home,
    icon: HomeIcon,
    labelKey: 'nav.dashboard',
    enabled: true,
    exactPath: true, // For exact path matching in navigation
    isDefault: true, // Default route redirect
  },
  {
    id: 'conversation-activities',
    path: '/conversation-activities',
    component: ConversationActivities,
    icon: LeaveMessageIcon,
    labelKey: 'nav.conversationActivities',
    enabled: true,
    requiredModules: ["portal.analytics"],
    // requiredModules: ['vip'],
  },
  {
    id: 'calendar',
    path: '/calendar',
    component: Calendar,
    icon: AppointmentIcon,
    labelKey: 'nav.appointment',
    enabled: true,
    requiredModules: ["portal.appointment"],
  },
  {
    id: 'patients',
    path: '/patients',
    component: Patients,
    icon: PatientsIcon,
    labelKey: 'nav.patients',
    enabled: true,
    subRoutes: [
      {
        path: '/patients/:patientId',
        component: PatientDetails,
      }
    ],
    requiredModules: ["portal.appointment"],
  },
  {
    id: 'doctors',
    path: '/doctors',
    component: Doctors,
    icon: DoctorIcon,
    labelKey: 'nav.doctors',
    enabled: true,
    requiredModules: ["structured_data"],
  },
  {
    id: 'locations',
    path: '/locations',
    component: Locations,
    icon: DoctorIcon, // Using DoctorIcon as placeholder, can be changed later
    labelKey: 'nav.locations',
    enabled: true,
    requiredModules: ["structured_data"],
  },
  {
    id: 'policy',
    path: '/policy',
    component: Policy,
    icon: QAIcon,
    labelKey: 'nav.qa',
    enabled: true,
    exactPath: true,
    requiredModules: ["qa_knowledge_base"],
  },
  {
    id: 'recordings',
    path: '/recordings',
    component: Recordings,
    icon: VoicemailIcon,
    labelKey: 'nav.voicemail',
    enabled: true,
    // Special routes that don't appear in nav but are related
    subRoutes: [
      {
        path: '/recordings/:recordingId',
        component: RecordingDetail,
      }
    ],
    requiredModules: ["portal.mctb"],
  },
  {
    id: 'call-logs',
    path: '/call-logs',
    component: CallLogs,
    icon: CallLogIcon,
    labelKey: 'nav.callLogs',
    enabled: true,
    requiredModules: ["portal.mctb"],
  },
  {
    id: 'appointment-reminder-jobs',
    path: '/appointment-reminder-jobs',
    component: AppointmentReminderJobs,
    icon: ReminderIcon,
    labelKey: 'nav.appointmentReminderJobs',
    enabled: true,
    requiredModules: ["portal.import"],
    subRoutes: [
      {
        path: '/appointment-reminder-jobs/:jobId/entries',
        component: AppointmentReminderJobEntries,
      }
    ]
  },
  {
    id: 'appointment-reminder-settings',
    path: '/appointment-reminder-settings',
    component: AppointmentReminderSettings,
    icon: ToolboxIcon,
    labelKey: 'nav.appointmentReminderSettings',
    enabled: true,
    exactPath: true,
    requiredModules: ["portal.import"],
  },
  // {
  //   id: 'conversations',
  //   path: '/conversations',
  //   component: Conversations,
  //   icon: ConversationIcon,
  //   labelKey: 'nav.conversations',
  //   enabled: true,
  //   requiredModules: ['vip'],
  // },
  {
    id: 'media',
    path: '/media',
    component: Media,
    icon: MediaIcon,
    labelKey: 'nav.media',
    enabled: false, // Re-enabled for demonstration
    requiredModules: ["portal.mctb"],
  },
  {
    id: 'import',
    path: '/import',
    component: Import,
    icon: ImportIcon,
    labelKey: 'nav.import',
    enabled: true,
    requiredModules: ["portal.import"],
  },
  {
    id: 'subscriptions',
    path: '/subscriptions',
    component: Subscriptions,
    icon: SubscriptionIcon,
    labelKey: 'nav.subscriptions',
    enabled: true,
    exactPath: true,
  },
  {
    id: 'analytics',
    path: '/analytics',
    component: Analytics,
    icon: ChartBarIcon,
    labelKey: 'nav.analytics',
    enabled: true,
    exactPath: true,
    requiredModules: ["portal.analytics"],
  },
  {
    id: 'meta-whatsapp-templates',
    path: '/meta-whatsapp-templates',
    component: MetaWhatsAppTemplates,
    icon: WhatsAppIcon,
    labelKey: 'nav.metaWhatsAppTemplates',
    enabled: true,
    exactPath: true,
    requiredModules: ["portal.coex"],
  },
  {
    id: 'embedded-signup',
    path: '/embedded-signup',
    component: EmbeddedSignup,
    icon: WhatsAppIcon,
    labelKey: 'nav.embeddedSignup',
    enabled: true,
    exactPath: true,
    requiredModules: ["portal.coex"],
  },
  {
    id: 'toolbox',
    path: '/toolbox',
    component: Toolbox,
    icon: ToolboxIcon,
    labelKey: 'nav.toolbox',
    enabled: true,
    exactPath: true,
    requiredModules: ["portal.appointment"],
  },
  {
    id: 'settings',
    path: '/settings',
    component: Settings,
    icon: SettingsIcon,
    labelKey: 'nav.settings',
    enabled: true,
    exactPath: true,
  },
  // {
  //   id: 'users',
  //   path: '/users',
  //   component: Users,
  //   icon: UsersIcon,
  //   labelKey: 'nav.users',
  //   enabled: true,
  //   exactPath: true,
  // },
];

/**
 * Get enabled navigation items for sidebar
 * @param {Function} hasModule - Function to check if a module is enabled
 * @param {string} clid - The clinic ID to check against requiredClid
 * @returns {Array} - The enabled navigation items
 */
export const getEnabledNavItems = (hasModule = () => true, clid = null) => {
  return navigationConfig.filter(item => {
    // Check if item is enabled
    if (!item.enabled) return false;

    // Check if item requires specific clid
    if (item.requiredClid && clid !== item.requiredClid) {
      return false;
    }

    // Check if item requires specific modules
    if (item.requiredModules && item.requiredModules.length > 0) {
      return item.requiredModules.every(module => hasModule(module));
    }

    return true;
  });
};

/**
 * Get all routes (enabled items + their sub-routes)
 * @param {Function} hasModule - Function to check if a module is enabled
 * @param {string} clid - The clinic ID to check against requiredClid
 */
export const getAllRoutes = (hasModule = () => true, clid = null) => {
  const routes = [];

  navigationConfig.forEach(item => {
    if (item.enabled) {
      // Check if item requires specific clid
      if (item.requiredClid && clid !== item.requiredClid) {
        return; // Skip this item if user doesn't have required clid
      }

      // Check if item requires specific modules
      if (item.requiredModules && item.requiredModules.length > 0) {
        if (!item.requiredModules.every(module => hasModule(module))) {
          return; // Skip this item if user doesn't have required modules
        }
      }

      // Add main route
      routes.push({
        path: item.path,
        component: item.component,
        id: item.id,
      });

      // Add sub-routes if they exist
      if (item.subRoutes) {
        item.subRoutes.forEach(subRoute => {
          routes.push({
            path: subRoute.path,
            component: subRoute.component,
            id: `${item.id}-sub`,
            parentId: item.id,
          });
        });
      }
    }
  });

  return routes;
};

/**
 * Get the default route path
 */
export const getDefaultRoute = () => {
  const defaultItem = navigationConfig.find(item => item.enabled && item.isDefault);
  return defaultItem ? defaultItem.path : '/home';
};

/**
 * Check if a path should be highlighted in navigation
 */
export const isPathActive = (navPath, currentPath, exactPath = false) => {
  if (exactPath) {
    return currentPath === navPath || (navPath === '/home' && currentPath === '/');
  }
  return currentPath.includes(navPath);
};

/**
 * Find a route configuration by path
 * @param {string} path - The path to find
 * @returns {Object|null} - The route configuration or null if not found
 */
export const findRouteByPath = (path) => {
  // First check main routes
  for (const item of navigationConfig) {
    if (item.path === path) {
      return item;
    }

    // Check sub-routes
    if (item.subRoutes) {
      for (const subRoute of item.subRoutes) {
        // Handle dynamic routes like /patients/:patientId
        if (subRoute.path.includes(':')) {
          const pattern = subRoute.path.replace(/:[^/]+/g, '[^/]+');
          const regex = new RegExp(`^${pattern}$`);
          if (regex.test(path)) {
            return { ...item, path: subRoute.path, component: subRoute.component };
          }
        } else if (subRoute.path === path) {
          return { ...item, path: subRoute.path, component: subRoute.component };
        }
      }
    }
  }

  return null;
};

/**
 * Check if a route path requires modules and if the user has them
 * @param {string} path - The path to check
 * @param {Function} hasModule - Function to check if a module is enabled
 * @param {string} clid - The clinic ID to check against requiredClid
 * @returns {boolean} - True if route is accessible, false if it requires modules or clid the user doesn't have
 */
export const isRouteAccessible = (path, hasModule = () => true, clid = null) => {
  const route = findRouteByPath(path);

  // If route doesn't exist, it's not accessible
  if (!route) {
    return false;
  }

  // If route is disabled, it's not accessible
  if (!route.enabled) {
    return false;
  }

  // If route requires specific clid, check if user has it
  if (route.requiredClid && clid !== route.requiredClid) {
    return false;
  }

  // If route requires modules, check if user has them
  if (route.requiredModules && route.requiredModules.length > 0) {
    return route.requiredModules.every(module => hasModule(module));
  }

  return true;
};
